/** The per-strategy capital account (2026-09-23).
 *
 * `GET /api/capital?strategy_id=` folds every closed trade through the
 * operator's own funding rules (`nss-research/docs/capital.md`): a shortfall
 * is deposited, the top-up comes back out once earned, and at twice the base
 * the base grows 1.5x and the rest is withdrawn.
 *
 * ⚠️ `due` is what the rules CALL FOR and the ledger has not recorded. It is
 * not a promise that money moved — the operator records that himself, because
 * the app must never claim a transfer that did not happen.
 */

import { fetchOrThrow, getJson } from "@/lib/http";

export interface CashMovement {
  id: string;
  on: string; // YYYY-MM-DD, IST
  flow: "DEPOSIT" | "WITHDRAWAL";
  amount: number;
  note?: string | null;
}

export interface CashDue {
  on: string;
  flow: "DEPOSIT" | "WITHDRAWAL";
  amount: number;
  why: string;
}

export interface CapitalAccount {
  strategy_id: string;
  /** What the account opened at, before any doubling grew the base. */
  started: number;
  base: number;
  balance: number;
  outstanding: number;
  /** Net of charges, and equal to the traded P&L by construction. */
  profit: number;
  gross_pnl: number;
  charges: number;
  deposited: number;
  withdrawn: number;
  sessions: number;
  first_session: string | null;
  movements: CashMovement[];
  due: CashDue[];
}

export const capitalApi = {
  async forStrategy(strategyId: string): Promise<CapitalAccount> {
    return getJson<CapitalAccount>(`/api/capital?strategy_id=${encodeURIComponent(strategyId)}`);
  },

  async record(body: {
    strategy_id: string;
    on: string;
    flow: "DEPOSIT" | "WITHDRAWAL";
    amount: number;
    note?: string;
  }): Promise<CashMovement> {
    const resp = await fetchOrThrow("/api/capital/movements", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return (await resp.json()) as CashMovement;
  },

  async remove(movementId: string): Promise<void> {
    await fetchOrThrow(`/api/capital/movements/${encodeURIComponent(movementId)}`, {
      method: "DELETE",
    });
  },
};
